import React, { useState, useMemo, useEffect } from 'react'
import { Grid } from '@mui/material'
import ReactSelect from 'react-select'
import {
  CONTENT_REFERENCE_TYPE_CONTENT_TO_TYPES,
  CONTENT_REFERENCE_TYPE_LABELS,
  CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
} from 'services/content-references/types'
import { BUNDLE_LABELS, BUNDLE_TYPES } from 'services/content/bundles'
import SectionAccordion from 'gui/components/accordion/section-accordion'
import type { ContentInfoChanges } from './types'

export interface FilterTypeSectionContentData {
  id?: string
  filterType?: string | null
  bundle?: string | null
}

export interface FilterTypeSectionProps {
  contentType: string
  contentData: FilterTypeSectionContentData | null
  isViewOnly: boolean
  isMulti: boolean
  bundleDisabled: boolean
  enableMarketingCenters?: unknown
  onDirtyChange: (isDirty: boolean) => void
  onChangesUpdate: (changes: ContentInfoChanges | null) => void
  onResetTriggered?: number
}

const NONE_OPTION = { label: 'None', value: null as string | null }

/**
 * Shared Filter Type section for tag and person detail pages.
 * Parent-managed: reports dirty and contentInput changes up; parent runs UPDATE_VIDEO_CONTENT.
 */
function FilterTypeSection (props: FilterTypeSectionProps) {
  const {
    contentType,
    contentData,
    isViewOnly,
    isMulti,
    bundleDisabled,
    enableMarketingCenters,
    onDirtyChange,
    onChangesUpdate,
    onResetTriggered = 0,
  } = props

  const [filterTypeValue, setFilterTypeValue] = useState<string>('')
  const [bundleValue, setBundleValue] = useState<string>('')

  const filterTypeOptions = useMemo(() => {
    const keys: string[] = []
    CONTENT_REFERENCE_TYPE_CONTENT_TO_TYPES.forEach((v, k) => {
      if (v === contentType && typeof k === 'string') {
        if (enableMarketingCenters === true || k !== CONTENT_REFERENCE_TYPE_MARKETING_CENTERS) {
          keys.push(k)
        }
      }
    })
    const sorted = keys.sort((a, b) => {
      const labelA = (CONTENT_REFERENCE_TYPE_LABELS.get(a, '') as string) || ''
      const labelB = (CONTENT_REFERENCE_TYPE_LABELS.get(b, '') as string) || ''
      return labelA.localeCompare(labelB)
    })
    const entries = sorted
      .map((k) => ({
        label: CONTENT_REFERENCE_TYPE_LABELS.get(k, '') as string,
        value: k,
      }))
      .filter((o) => o.label !== '')
    return [NONE_OPTION, ...entries]
  }, [contentType, enableMarketingCenters])

  const bundleOptions = useMemo(() => {
    const keys: string[] = []
    BUNDLE_TYPES.forEach((v, k) => {
      if (v === contentType && typeof k === 'string') keys.push(k)
    })
    const sorted = keys.sort((a, b) => {
      const labelA = (BUNDLE_LABELS.get(a, '') as string) || ''
      const labelB = (BUNDLE_LABELS.get(b, '') as string) || ''
      return labelA.localeCompare(labelB)
    })
    const entries = sorted
      .map((k) => ({
        label: BUNDLE_LABELS.get(k, '') as string,
        value: k,
      }))
      .filter((o) => o.label !== '')
    return [NONE_OPTION, ...entries]
  }, [contentType])

  useEffect(() => {
    if (contentData) {
      setFilterTypeValue(contentData.filterType ?? '')
      setBundleValue(contentData.bundle ?? '')
    }
  }, [contentData, onResetTriggered])

  const reportChange = (filterType: string, bundle: string) => {
    const id = contentData?.id
    if (id == null) return
    onDirtyChange(true)
    onChangesUpdate({
      contentInput: {
        id,
        filterType,
        bundle,
      },
      generatePath: false,
    })
  }

  return (
    <SectionAccordion title="Filter Type" collapsed>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6}>
          <ReactSelect
            className="content-detail__select"
            name="filterType"
            id="filterType"
            isMulti={isMulti}
            value={
              isMulti
                ? filterTypeOptions.filter(
                    (o) =>
                      o.value &&
                      filterTypeValue
                        .split(',')
                        .map((s) => s.trim())
                        .includes(o.value),
                  )
                : (filterTypeOptions.find((o) => o.value === filterTypeValue) ?? NONE_OPTION)
            }
            options={filterTypeOptions}
            onChange={(selected) => {
              let newFilterType: string
              if (isMulti) {
                newFilterType = Array.isArray(selected)
                  ? selected
                      .map((o) => o.value)
                      .filter((v): v is string => v != null && v !== '')
                      .join(',')
                  : ''
              } else {
                newFilterType = (selected as { value: string | null })?.value ?? ''
              }
              setFilterTypeValue(newFilterType)
              reportChange(newFilterType, bundleValue)
            }}
            isDisabled={isViewOnly}
          />
        </Grid>
        <Grid item xs={6}>
          <ReactSelect
            className="content-detail__select"
            name="bundle"
            id="bundle"
            value={bundleOptions.find((o) => o.value === bundleValue) ?? NONE_OPTION}
            options={bundleOptions}
            isDisabled={isViewOnly || bundleDisabled}
            onChange={(selected) => {
              const newBundle = (selected as { value: string | null })?.value ?? ''
              setBundleValue(newBundle)
              reportChange(filterTypeValue, newBundle)
            }}
          />
        </Grid>
      </Grid>
    </SectionAccordion>
  )
}

export default FilterTypeSection
